import { Injectable, effect, signal } from '@angular/core';

export const storageKey = 'theme';

export type Tema = 'light-theme' | 'dark-theme';

/**
 * Tema chiaro/scuro del pannello.
 *
 * Un solo punto scrive lo stato: l'`effect` osserva il segnale, salva la
 * scelta e mette la classe sul body. La versione precedente invertiva la
 * classe presente invece di scrivere quella giusta, e lo faceva sia
 * all'avvio sia dall'effect: con il tema scuro in memoria le due inversioni
 * si annullavano e la pagina si apriva chiara.
 */
@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  readonly theme = signal<Tema>(this.temaSalvato());

  /** Alias storico, usato dove il tema si legge come stream. */
  readonly theme$ = this.theme;

  readonly #body: HTMLBodyElement | null = document.querySelector('body');

  constructor() {
    effect(() => {
      const tema = this.theme();
      localStorage.setItem(storageKey, tema);
      this.applicaTema(tema);
    });
  }

  toggleTheme(): void {
    this.theme.update((corrente) => (corrente === 'dark-theme' ? 'light-theme' : 'dark-theme'));
  }

  isDarkThemeActive(): boolean {
    return this.theme() === 'dark-theme';
  }

  getToggleLabel(): string {
    return this.isDarkThemeActive() ? 'Passa al tema chiaro' : 'Passa al tema scuro';
  }

  private temaSalvato(): Tema {
    return localStorage.getItem(storageKey) === 'dark-theme' ? 'dark-theme' : 'light-theme';
  }

  private applicaTema(tema: Tema): void {
    const classi = this.#body?.classList;
    if (!classi) return;
    classi.toggle('dark-theme', tema === 'dark-theme');
    classi.toggle('light-theme', tema === 'light-theme');
  }
}
